import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../../../lib/format";

interface ClientStat {
  id: number; name: string; email: string | null;
  language: "en" | "th"; credit_terms_days: number;
  open_count: number; open_total: number; open_sms: number; last_sent_at: string | null;
}

export default function Dashboard() {
  const { current } = useCurrentCompany();
  const [stats, setStats] = useState<ClientStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!current) return;
    setLoading(true);
    api<{ clients: ClientStat[] }>(`/api/collection/clients?company_id=${current.id}`)
      .then((r) => setStats(r.clients.filter((c) => c.open_count > 0)))
      .finally(() => setLoading(false));
  }, [current]);

  const totals = stats.reduce(
    (acc, c) => { acc.count += c.open_count; acc.amount += c.open_total; acc.sms += c.open_sms || 0; return acc; },
    { count: 0, amount: 0, sms: 0 },
  );

  const currency = current?.currency || "THB";

  const filteredSorted = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? stats.filter((c) => c.name.toLowerCase().includes(needle) || (c.email || "").toLowerCase().includes(needle))
      : stats;
    return filtered.slice().sort((a, b) => b.open_total - a.open_total);
  }, [stats, q]);

  return (
    <div className="space-y-7">
      <div>
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Dashboard</h1>
            <p className="mt-1 text-sm text-slate-500">{current?.name || "—"}</p>
          </div>
          <Link to="/collection/send" className="btn-primary !rounded-xl !px-6 !py-2.5">
            Send reminders
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Clients with open balance" value={String(stats.length)}     accent="from-sky-50 to-white" />
        <MetricCard label="Open invoices"             value={String(totals.count)}     accent="from-indigo-50 to-white" />
        <MetricCard label="Total outstanding"         value={fmtMoney(totals.amount, currency)} accent="from-violet-50 to-white" emphasis />
        <MetricCard label="Total SMS"                 value={totals.sms.toLocaleString()} accent="from-emerald-50 to-white" />
      </div>

      <section className="card overflow-hidden">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
          <div>
            <h2 className="section-title">Top open balances</h2>
            <p className="text-xs text-slate-500">
              {q ? `${filteredSorted.length} match${filteredSorted.length === 1 ? "" : "es"}` : "Sorted by outstanding amount, largest first"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-72">
              <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>
              <input
                className="search-input w-full !h-9"
                placeholder="Search by client name or email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Link to="/collection/clients" className="text-xs font-medium text-slate-500 hover:text-slate-900">
              View all clients →
            </Link>
          </div>
        </header>
        <table className="refined">
          <thead>
            <tr>
              <th>Client</th>
              <th>Lang</th>
              <th>Open</th>
              <th className="text-right">Total</th>
              <th>Last reminder</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && filteredSorted.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                <div className="font-medium">{q ? "No matches" : "All clear."}</div>
                <div className="text-xs text-slate-400">
                  {q ? "Try a different search term." : "No open invoices. Import a new QBO export to refresh."}
                </div>
              </td></tr>
            )}
            {filteredSorted.slice(0, q ? 200 : 12).map((c) => (
              <tr key={c.id}>
                <td>
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.email || <span className="text-rose-500">missing email</span>}</div>
                </td>
                <td><span className="pill pill-slate uppercase">{c.language}</span></td>
                <td className="text-slate-700">{c.open_count}</td>
                <td className="text-right font-mono font-semibold text-slate-900">{fmtMoney(c.open_total, currency)}</td>
                <td className="text-xs text-slate-500">{c.last_sent_at ? fmtDate(c.last_sent_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function MetricCard({
  label, value, accent, emphasis,
}: {
  label: string;
  value: string;
  accent: string;
  emphasis?: boolean;
}) {
  return (
    <div className={`card card-hover relative overflow-hidden p-5`}>
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accent} opacity-70`} />
      <div className="relative">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`mt-2 ${emphasis ? "text-3xl" : "text-2xl"} font-semibold tracking-tightish text-slate-900`}>
          {value}
        </div>
      </div>
    </div>
  );
}
